import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../../components';
import VideoPlayer from '../../components/VideoPlayer';
import { getCourseById, CourseDoc, ItemDoc } from '../../services/course-service';
import { markItemComplete } from '../../services/enrollment-service';

const Lesson = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get('courseId');

  const [course, setCourse] = useState<CourseDoc | null>(null);
  const [item, setItem] = useState<ItemDoc | null>(null);
  const [loading, setLoading] = useState(true);

  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState<Array<{ role: 'user' | 'ai'; text: string }>>([
    { role: 'ai', text: 'Hi! Ask me anything about this lesson and I will help.' }
  ]);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!courseId || !id) { setLoading(false); return; }
    getCourseById(courseId).then(c => {
      setCourse(c);
      for (const mod of c.modules || []) {
        const found = mod.items.find(i => String(i._id) === id);
        if (found) { setItem(found); break; }
      }
      markItemComplete(courseId, id).catch(() => {});
    }).catch(() => {}).finally(() => setLoading(false));
  }, [courseId, id]);

  if (loading) return <div className="section-gap"><p className="text-muted">Loading lesson...</p></div>;

  if (!item || !course) {
    return (
      <div className="section-gap">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-text mb-4">Lesson Not Found</h2>
          <p className="text-muted mb-6">The lesson you're looking for doesn't exist.</p>
          <Link to="/app/lms/courses"><Button variant="primary">Back to Courses</Button></Link>
        </div>
      </div>
    );
  }

  const handleSend = () => {
    if (!chatInput.trim()) return;
    setChatMessages(prev => [...prev, { role: 'user', text: chatInput.trim() }, { role: 'ai', text: 'Thanks for your question — here\'s a helpful tip for this lesson.' }]);
    setChatInput('');
  };

  const backLink = `/app/lms/courses/${courseId}`;

  return (
    <div className="section-gap">
      <div className="border-b border-line pb-4">
        <div className="flex items-center gap-3 mb-3">
          <Link to={backLink} className="text-muted hover:text-text">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </Link>
          <span className="text-muted text-sm">Back to {course.title}</span>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text mb-2">{item.title}</h1>
            <div className="flex items-center gap-4 text-sm text-muted">
              {item.duration ? <span>{item.duration} min</span> : null}
              <span className="bg-surface/50 px-2 py-0.5 rounded">{item.type}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="two-col">
        <div className="section-gap">
          {item.type === 'video' && item.fileUrl ? (
            <VideoPlayer src={item.fileUrl} className="w-full rounded-lg aspect-video" />
          ) : item.type === 'video' && (item as any).videoUrl ? (
            <div className="relative w-full rounded-lg overflow-hidden bg-black" style={{ aspectRatio: '16/9' }}>
              <iframe
                src={(() => {
                  try {
                    const u = new URL((item as any).videoUrl);
                    const id = u.hostname.includes('youtu.be') ? u.pathname.slice(1).split('?')[0] : u.searchParams.get('v') || '';
                    return id ? `https://www.youtube-nocookie.com/embed/${id}?rel=0` : '';
                  } catch { return ''; }
                })()}
                className="w-full h-full"
                allowFullScreen
                title={item.title}
              />
            </div>
          ) : item.type === 'text' && item.textContent ? (
            <div className="prose max-w-none text-muted leading-relaxed border border-line rounded-xl p-6" dangerouslySetInnerHTML={{ __html: item.textContent }} />
          ) : item.type === 'pdf' && item.fileUrl ? (
            <embed src={item.fileUrl} type="application/pdf" className="w-full rounded-lg" style={{ height: '70vh' }} />
          ) : (
            <div className="border border-line rounded-xl h-[320px] flex items-center justify-center">
              <div className="text-center">
                <svg className="w-16 h-16 text-muted mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                </svg>
                <p className="text-muted">Content placeholder</p>
              </div>
            </div>
          )}

          <div className="border border-line rounded-xl flex flex-col h-[300px] min-h-0 p-4">
            <div className="text-text font-semibold mb-2">Ask AI about this lesson</div>
            <div className="flex-1 overflow-auto text-sm text-muted space-y-3">
              {chatMessages.map((m, i) => (
                <div key={i} className={`${m.role === 'user' ? 'text-right' : 'text-left'}`}>
                  <div className={`inline-block px-3 py-2 rounded-lg ${m.role === 'user' ? 'bg-chart text-white' : 'bg-surface/50 text-muted border border-line'}`}>{m.text}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <input
                className="flex-1 bg-surface/50 border border-line rounded-lg px-3 py-2 text-sm text-text placeholder:text-muted focus:outline-none focus:border-chart/50"
                placeholder="Ask a question..."
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSend()}
              />
              <button onClick={handleSend} className="h-9 px-4 rounded-lg border border-line text-sm text-muted hover:text-text hover:border-black/15 transition-colors">Send</button>
            </div>
          </div>
        </div>

        <div className="section-gap">
          <div className="border border-line rounded-xl p-4 flex flex-col h-[200px] min-h-0">
            <div className="text-text font-semibold mb-2">Your Notes</div>
            <textarea
              className="flex-1 bg-surface/50 border border-line rounded-lg p-3 text-sm text-text placeholder:text-muted resize-none focus:outline-none focus:border-chart/50"
              placeholder="Write notes here..."
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Lesson;
